import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, 
  Settings, 
  Wrench, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Zap,
  Shield,
  Building,
  Radio,
  Sparkles,
  Globe,
  Cpu,
  Network,
  ChevronLeft,
  ChevronRight,
  Phone,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../utils/cn';

const Services = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // State for division slider
  const [currentDivisionSlide, setCurrentDivisionSlide] = useState(0);
  const divisionSliderRef = useRef(null);

  const mainServices = [
    {
      icon: ShoppingCart,
      title: t('services.procurement.title'),
      description: t('services.procurement.description'),
      features: [
        'Global sourcing and vendor management',
        'Quality assurance and inspection',
        'Supply chain optimization',
        'Cost-effective procurement solutions'
      ],
      color: 'global-green',
      bgImage: '/divisions/Commercial - Electrical/shopping-cart-full-of-electrical-components-and-eq-2023-11-27-05-21-03-utc.jpg',
      glowColor: 'global-green'
    },
    {
      icon: Settings,
      title: t('services.turnkey.title'),
      description: t('services.turnkey.description'),
      features: [
        'Complete project management',
        'Design and engineering',
        'Installation and commissioning',
        'Post-delivery support'
      ],
      color: 'global-dark-blue',
      bgImage: '/divisions/Commercial - Electrical/robotic-arm-modern-industrial-technology-automate-2023-11-27-05-21-51-utc.jpg',
      glowColor: 'global-dark-blue'
    },
    {
      icon: Wrench,
      title: t('services.installation.title'),
      description: t('services.installation.description'),
      features: [
        'Professional installation services',
        'Comprehensive testing protocols',
        'Quality assurance procedures',
        'Performance validation'
      ],
      color: 'global-light-blue',
      bgImage: '/divisions/Commercial - Electrical/the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg',
      glowColor: 'global-light-blue'
    },
    {
      icon: Users,
      title: t('services.engineering.title'),
      description: t('services.engineering.description'),
      features: [
        'Technical consultation',
        'System design and optimization',
        'Training and knowledge transfer',
        'Ongoing technical support'
      ],
      color: 'charcoal',
      bgImage: '/divisions/General/diverse-people-business-meeting-shoot-2023-11-27-05-02-23-utc.jpg',
      glowColor: 'slate'
    }
  ];

  const divisions = [
    {
      key: 'telecommunication',
      icon: Phone,
      path: '/divisions/telecommunication',
      color: 'from-primary to-accent',
      folder: 'Telecommunications',
      images: [
        'communications-tower-2024-10-22-17-32-42-utc.jpg',
        'data-center-with-multiple-rows-of-fully-operationa-2023-11-27-05-16-14-utc.jpg',
        'fiber-optics-2024-10-21-03-46-49-utc.jpg',
        'telecommunication-equipment-for-5g-radio-network-2024-12-03-04-12-51-utc.jpg',
        'bundle-of-fibre-optic-cables-2024-06-20-03-08-53-utc.jpg'
      ]
    },
    {
      key: 'power',
      icon: Zap,
      path: '/divisions/power',
      color: 'from-primary to-accent',
      folder: 'Power - Solar',
      images: [
        'solar-panels-producing-green-renewable-energy-sol-2023-11-27-05-31-29-utc.jpg',
        'electrical-substation-distribution-power-plant-wit-2024-07-25-01-00-57-utc.jpg',
        'engineer-in-charge-of-solar-panel-installation-the-2023-11-27-05-02-11-utc.jpg',
        'arrays-of-blue-solar-panels-2023-11-27-05-06-18-utc.jpg',
        'power-plant-near-coast-2023-11-27-05-22-28-utc.jpg'
      ]
    },
    {
      key: 'hazardous',
      icon: Shield,
      path: '/divisions/hazardous',
      color: 'from-primary to-accent',
      folder: 'Hazardous',
      images: [
        'industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg',
        'chemical-plant-for-production-of-ammonia-on-night-2023-11-27-05-18-08-utc.jpg',
        'oil-refinery-industrial-plant-at-night-2023-11-27-05-19-35-utc.jpg',
        'petrochemical-plant-in-the-evening-2023-11-27-05-20-24-utc.jpg'
      ]
    },
    {
      key: 'commercial',
      icon: Building2,
      path: '/divisions/commercial',
      color: 'from-primary to-accent',
      folder: 'Commercial - Electrical',
      images: [
        'man-an-electrical-technician-working-in-a-switchb-2023-11-27-04-54-33-utc.jpg',
        'the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg',
        'robotic-arm-modern-industrial-technology-automate-2023-11-27-05-21-51-utc.jpg',
        'hvac-technician-installing-large-modern-heat-pump-2023-11-27-05-04-45-utc.jpeg',
        'vertical-of-a-power-station-for-power-supply-2023-11-27-05-27-38-utc.jpg'
      ]
    }
  ];

  // Function to get current division image
  const getCurrentDivisionImage = (division, imageIndex) => {
    if (division.images && division.images[imageIndex]) {
      return `/divisions/${division.folder}/${division.images[imageIndex]}`;
    }
    return '/divisions/General/dubai-downtown-skyline-united-arab-emirates-or-ua-2024-10-18-11-06-55-utc.jpg';
  };

  // Auto-cycle division slider every 12 seconds for better visibility
  useEffect(() => {
    const divisionInterval = setInterval(() => {
      setCurrentDivisionSlide((prevSlide) => 
        prevSlide === divisions.length - 1 ? 0 : prevSlide + 1
      );
    }, 12000);
    
    return () => clearInterval(divisionInterval);
  }, [divisions.length]);

  const processSteps = [
    {
      step: '01',
      title: 'Consultation',
      description: 'Understanding your requirements and challenges',
      icon: Globe
    },
    {
      step: '02',
      title: 'Planning',
      description: 'Developing comprehensive project plans and strategies',
      icon: Cpu
    },
    {
      step: '03',
      title: 'Execution',
      description: 'Implementing solutions with precision and quality',
      icon: Settings
    },
    {
      step: '04',
      title: 'Support',
      description: 'Providing ongoing support and maintenance',
      icon: Network
    }
  ];

  return (
    <div className="min-h-screen pt-16 bg-white">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden text-white h-[70vh]">
        {/* Background Image */}
       <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/divisions/General/diverse-people-business-meeting-shoot-2023-11-27-05-02-23-utc.jpg')`
          }}
        ></div>
        
        {/* Futuristic Overlay */}
        <div className="absolute inset-0 bg-global-dark-blue/90"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 right-20 w-40 h-40 bg-global-light-blue/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.7, 0.3],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-32 h-32 bg-global-green/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.2, 0.6, 0.2],
              rotate: [360, 180, 0]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 3
            }}
          />
          
          {/* Floating particles */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, -100, -20],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 pt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <span className="block pb-2">{t('services.title')}</span>
              <motion.span 
                className="pb-5 block text-global-light-blue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
              >
                & Solutions
              </motion.span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-gray-200"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              {t('services.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-padding relative bg-white overflow-hidden">
        {/* Futuristic Grid Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(46, 46, 46, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(46, 46, 46, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
           
            
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Our Core Services
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Comprehensive engineering solutions tailored to meet your specific needs and industry requirements.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {mainServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  {/* Hero-style Card with Polygon Design */}
                  <div className="relative h-[400px] overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 bg-white">
                    {/* Right Side - Service Image (Background) */}
                    <div className="absolute inset-0 h-full overflow-hidden">
                      <motion.div
                        className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"
                        style={{ backgroundImage: `url('${service.bgImage}')` }}
                        initial={{ opacity: 0, scale: 1.1 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                      />
                      {/* Overlay for better contrast */}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors duration-500" />
                    </div>

                    <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 h-full">
                      {/* Left Side - Polygon Shape with Content (Overlapping) */}
                      <div className="relative h-full lg:col-span-1">
                        {/* Polygon Shape with Hero-style Design - Overlapping */}
                        <div 
                          className={`absolute inset-0 bg-gradient-to-br from-${service.color} via-${service.color} to-${service.glowColor} z-10 w-full h-full lg:w-[140%]`}
                          style={{
                            clipPath: 'polygon(0 0, 100% 0, 75% 100%, 0% 100%)'
                          }}
                        >
                          {/* Clean Geometric Pattern */}
                          <div 
                            className="absolute inset-0 opacity-15"
                            style={{
                              backgroundImage: `
                                repeating-linear-gradient(
                                  45deg,
                                  transparent,
                                  transparent 20px,
                                  rgba(255,255,255,0.3) 20px,
                                  rgba(255,255,255,0.3) 22px,
                                  transparent 22px,
                                  transparent 40px,
                                  rgba(255,255,255,0.2) 40px,
                                  rgba(255,255,255,0.2) 42px
                                ),
                                repeating-linear-gradient(
                                  -45deg,
                                  transparent,
                                  transparent 30px,
                                  rgba(255,255,255,0.2) 30px,
                                  rgba(255,255,255,0.2) 32px,
                                  transparent 32px,
                                  transparent 60px,
                                  rgba(255,255,255,0.1) 60px,
                                  rgba(255,255,255,0.1) 62px
                                )
                              `,
                              backgroundSize: '80px 80px, 100px 100px'
                            }}
                          />
                          {/* Additional Geometric Lines */}
                          <div 
                            className="absolute inset-0 opacity-8"
                            style={{
                              backgroundImage: `
                                repeating-linear-gradient(
                                  0deg,
                                  transparent,
                                  transparent 50px,
                                  rgba(255,255,255,0.15) 50px,
                                  rgba(255,255,255,0.15) 51px
                                ),
                                repeating-linear-gradient(
                                  90deg,
                                  transparent,
                                  transparent 50px,
                                  rgba(255,255,255,0.15) 50px,
                                  rgba(255,255,255,0.15) 51px
                                )
                              `
                            }}
                          />
                        </div>
                        
                        {/* Content over polygon */}
                        <motion.div
                          className="relative z-20 text-white p-6 lg:p-8 h-full flex flex-col justify-center"
                          initial={{ opacity: 0, x: -50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.8 }}
                          viewport={{ once: true }}
                        >
                          
                          <motion.h3 
                            className="text-2xl font-bold mb-3 text-white"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            viewport={{ once: true }}
                          >
                            {service.title}
                          </motion.h3>
                          
                          <motion.p 
                            className="text-white/90 mb-4 text-sm leading-relaxed"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            viewport={{ once: true }}
                          >
                            {service.description}
                          </motion.p>
                          
                          <motion.ul 
                            className="space-y-2"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                            viewport={{ once: true }}
                          >
                            {service.features.slice(0, 3).map((feature, featureIndex) => (
                              <motion.li 
                                key={featureIndex} 
                                className="flex items-center text-white/90 text-sm"
                                initial={{ opacity: 0, x: -10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: featureIndex * 0.1 + 0.6 }}
                                viewport={{ once: true }}
                              >
                                <CheckCircle className="w-4 h-4 text-white/80 mr-3 flex-shrink-0" />
                                {feature}
                              </motion.li>
                            ))}
                          </motion.ul>
                        </motion.div>
                      </div>
                      
                      {/* Right Side - Empty space for overlapping effect */}
                      <div className="relative h-full lg:col-span-1">
                        {/* Floating Icon */}
                        <motion.div
                          className="absolute top-6 right-6 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center z-30"
                          initial={{ opacity: 0, scale: 0 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6, delay: 0.8 }}
                          viewport={{ once: true }}
                          whileHover={{ scale: 1.1 }}
                        >
                          <ArrowRight className="w-5 h-5 text-white" />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium">
              <Network className="w-4 h-4" />
              Our Process
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Our Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              A systematic approach to delivering exceptional results for every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="relative mb-8">
                    
                    
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto border border-gray-200">
                      <StepIcon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Suppliers Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Our Trusted Suppliers
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              We partner with industry-leading suppliers to deliver the highest quality products and solutions to our clients worldwide.
            </p>
          </motion.div>

          {/* Suppliers Grid */}
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              { name: 'ABB', logo: '/Suppliers logo/ABB.png' },
              { name: 'APC', logo: '/Suppliers logo/APC.png' },
              { name: 'Allen Bradley', logo: '/Suppliers logo/Allen-Bradley_logo.png' },
              { name: 'Baldor', logo: '/Suppliers logo/Baldor.png' },
              { name: 'Belden', logo: '/Suppliers logo/Belden.png' },
              { name: 'Borri', logo: '/Suppliers logo/Borri.png' },
              { name: 'British General', logo: '/Suppliers logo/British General.jpg' },
              { name: 'CEG', logo: '/Suppliers logo/CEG logo.png' },
              { name: 'Ducab', logo: '/Suppliers logo/Ducab.jpg' },
              { name: 'Eaton', logo: '/Suppliers logo/Eaton.jpg' },
              { name: 'Enersys', logo: '/Suppliers logo/Enersys.jpeg' },
              { name: 'Fluke', logo: '/Suppliers logo/Fluke.png' },
              { name: 'GE', logo: '/Suppliers logo/GE.png' },
              { name: 'GNB', logo: '/Suppliers logo/GNB.png' },
              { name: 'Gewiss', logo: '/Suppliers logo/Gewiss.jpg' },
              { name: 'Gutor', logo: '/Suppliers logo/Gutor.png' },
              { name: 'Hioki', logo: '/Suppliers logo/Hioki.png' },
              { name: 'Honeywell', logo: '/Suppliers logo/Honeywell-Logo.png' },
              { name: 'Hoppecke', logo: '/Suppliers logo/Hoppecke.png' },
              { name: 'Kepco', logo: '/Suppliers logo/Kepco_Logo.png' },
              { name: 'Kyocera', logo: '/Suppliers logo/Kyocera_logo.svg.png' },
              { name: 'La Marche', logo: '/Suppliers logo/La Marche.png' },
              { name: 'Lexseco', logo: '/Suppliers logo/Lexseco.png' },
              { name: 'MK Electric', logo: '/Suppliers logo/MK Electric.jpg' },
              { name: 'Moxa', logo: '/Suppliers logo/Moxa.png' },
              { name: 'Oman Cable', logo: '/Suppliers logo/Oman cable.jpg' },
              { name: 'Osram', logo: '/Suppliers logo/Osram.jpg' },
              { name: 'Panasonic', logo: '/Suppliers logo/Panasonic.jpg' },
              { name: 'Philips', logo: '/Suppliers logo/Philips.jpg' },
              { name: 'Prysmian', logo: '/Suppliers logo/Prysmian.jpg' },
              { name: 'Riyadh Cables', logo: '/Suppliers logo/Riyadh cables.jpg' },
              { name: 'Saft', logo: '/Suppliers logo/Saft.png' },
              { name: 'Sonnenschein', logo: '/Suppliers logo/Sonnenschein_Logo.jpg' },
              { name: 'Statron', logo: '/Suppliers logo/Statron.png' },
              { name: 'AEG Power Solutions', logo: '/Suppliers logo/aeg-power-solutions.png' },
              { name: 'Alcad', logo: '/Suppliers logo/alcad-logo.jpg' },
              { name: 'Benning', logo: '/Suppliers logo/benning.png' },
              { name: 'Emerson', logo: '/Suppliers logo/emerson.png' },
              { name: 'Exide', logo: '/Suppliers logo/exide-logo-png.png' },
              { name: 'Legrand', logo: '/Suppliers logo/legrand-logo.png' },
              { name: 'Leviton', logo: '/Suppliers logo/leviton-logo.jpg' },
              { name: 'Mean Well', logo: '/Suppliers logo/mean-well.png' },
              { name: 'Megger', logo: '/Suppliers logo/megger_logo.jpg' },
              { name: 'Mitsubishi Electric', logo: '/Suppliers logo/mitsubishi-electric.png' },
              { name: 'Nidec', logo: '/Suppliers logo/nidec-logo.jpg' },
              { name: 'nVent', logo: '/Suppliers logo/nvent.png' },
              { name: 'Phoenix Contact', logo: '/Suppliers logo/phoenix-contact-logo.png' },
              { name: 'Powersonic', logo: '/Suppliers logo/powrsonic.png' },
              { name: 'Schneider Electric', logo: '/Suppliers logo/schneider-logo.png' },
              { name: 'Siemens', logo: '/Suppliers logo/siemens.png' },
              { name: 'SKF', logo: '/Suppliers logo/skf_logo.png' },
              { name: 'Thermon', logo: '/Suppliers logo/thermon.png' },
              { name: 'Toshiba', logo: '/Suppliers logo/toshiba.jpg' },
              { name: 'WEG', logo: '/Suppliers logo/weg-logo.png' }
            ].map((supplier, index) => (
              <motion.div
                key={supplier.name}
                variants={fadeInUp}
                className="group"
              >
                <Card className="h-32 bg-white border border-gray-200 hover:border-global-green hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                  <CardContent className="p-4 h-full flex items-center justify-center">
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={supplier.logo}
                        alt={supplier.name}
                        className="max-w-full max-h-full object-contain transition-all duration-300 group-hover:scale-110"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="hidden w-full h-full items-center justify-center text-center"
                        style={{ display: 'none' }}
                      >
                        <span className="text-sm font-medium text-gray-600">{supplier.name}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Partnership Statement */}
          <motion.div
            className="text-center mt-16"
            {...fadeInUp}
          >
            <div className="bg-global-green/10 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-charcoal mb-4">
                Quality Through Partnership
              </h3>
              <p className="text-lg text-slate max-w-4xl mx-auto">
                Our strategic partnerships with these world-class suppliers enable us to provide cutting-edge solutions, 
                ensuring reliability, innovation, and excellence in every project we deliver.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white text-global-green">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Global Technology?
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Experience the difference of working with industry leaders committed to your success.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Proven Expertise',
                description: '25+ years of experience delivering complex engineering solutions across multiple industries.',
                icon: CheckCircle
              },
              {
                title: 'Global Reach',
                description: 'International presence with local expertise, ensuring comprehensive support worldwide.',
                icon: CheckCircle
              },
              {
                title: 'Quality Assurance',
                description: 'ISO certified processes ensuring the highest standards of quality and reliability.',
                icon: CheckCircle
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Icon className="w-12 h-12 text-accent mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="opacity-90">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 relative overflow-hidden">        
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-5 left-10 w-20 h-20 bg-teal-400/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-24 h-24 bg-accent/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
        
        <div className="container-max relative z-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center min-h-[400px]">
            {/* Left Side - Polygon Design Pattern */}
            <div className="relative h-full lg:col-span-2">
              {/* Polygon Shape with Hero-style Design */}
              <div 
                className="absolute inset-0 bg-global-green z-5 w-[1000px] h-full"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 70% 100%, 0% 100%)'
                }}
              >
                {/* Clean Geometric Pattern */}
                <div 
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 20px,
                        rgba(255,255,255,0.3) 20px,
                        rgba(255,255,255,0.3) 22px,
                        transparent 22px,
                        transparent 40px,
                        rgba(255,255,255,0.2) 40px,
                        rgba(255,255,255,0.2) 42px
                      ),
                      repeating-linear-gradient(
                        -45deg,
                        transparent,
                        transparent 30px,
                        rgba(255,255,255,0.2) 30px,
                        rgba(255,255,255,0.2) 32px,
                        transparent 32px,
                        transparent 60px,
                        rgba(255,255,255,0.1) 60px,
                        rgba(255,255,255,0.1) 62px
                      )
                    `,
                    backgroundSize: '80px 80px, 100px 100px'
                  }}
                />
                {/* Additional Geometric Lines */}
                <div 
                  className="absolute inset-0 opacity-8"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      ),
                      repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      )
                    `
                  }}
                />
              </div>
              
              {/* Content over polygon */}
              <motion.div
                className="relative z-20 text-white p-6 lg:p-8 h-full flex flex-col justify-center"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="inline-block mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30">
                    Ready to Get Started?
                  </span>
                </motion.div>
                
                <motion.h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Ready to Start Your Project?
                </motion.h2>
                
                <motion.p 
                  className="text-base lg:text-lg mb-6 text-gray-100 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Get in touch with our experts to discuss your engineering needs
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Link to="/contact">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button size="lg" className="bg-black text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                        {t('common.contactUs')}
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                        </motion.div>
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/services">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10 hover:border-white backdrop-blur-sm transition-all duration-300">
                        {t('common.getQuote')}
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;